import type {ScriptBlockData} from '../components/story/ScriptBlock';
import {replaceBlockText,observedTextEdit,mergeRichParagraphs, validateRichText, type ScriptRichText} from './scriptRichText';
import {validateAlternates,type ScriptAlternates} from './scriptAlternates';

export type DualSide = 'left' | 'right';
export type DualParagraphType = 'character' | 'dialogue' | 'parenthetical';
export interface DualParagraph {
    id:string; type:DualParagraphType; content:string; richText?:ScriptRichText; alternates?:ScriptAlternates;
}
export interface ScriptDualDialogue {
    schemaVersion:1; paragraphAttributes:Record<string,string>;
    left:DualParagraph[]; right:DualParagraph[];
}
export const dualText = (data:ScriptDualDialogue):string =>
    [data.left.map(p => p.content).join('\n'),data.right.map(p => p.content).join('\n')].join('\n\n');

export function validateDualDialogue(value:unknown, content?:string):asserts value is ScriptDualDialogue {
    const data=value as ScriptDualDialogue;
    const attrs=(v:unknown) => !!v && typeof v==='object' && !Array.isArray(v) && Object.values(v).every(x => typeof x==='string');
    if (!value || typeof value!=='object' || Array.isArray(value) || data.schemaVersion!==1 || !attrs(data.paragraphAttributes))
        throw new Error('Unsupported or invalid dual dialogue.');
    const ids=new Set<string>();
    for (const side of ['left','right'] as const) {
        if (!Array.isArray(data[side]) || data[side].length<2 || data[side][0]?.type!=='character'
            || side==='left' && data[side].filter(p => p?.type==='character').length!==1
            || !data[side].some(p => p?.type==='dialogue')) throw new Error('Each dual stream needs a character cue and dialogue; the first stream has one cue.');
        for (const paragraph of data[side]) {
            if (!paragraph || typeof paragraph.id!=='string' || !paragraph.id || ids.has(paragraph.id)
                || 'dualDialogue' in paragraph || !['character','dialogue','parenthetical'].includes(paragraph.type) || typeof paragraph.content!=='string')
                throw new Error('Invalid or duplicate dual paragraph.');
            ids.add(paragraph.id); validateRichText(paragraph.richText,paragraph.content);
            validateAlternates(paragraph.alternates);
        }
    }
    if (content!==undefined && content!==dualText(data)) throw new Error('Dual dialogue text does not match its paragraph streams.');
}

/** Legacy four-field JSON is read without a cloud rewrite. It becomes typed
 * paragraph data only when the user edits it. Invalid data remains an error. */
export function dualFor(block:ScriptBlockData):ScriptDualDialogue {
    if (block.dualDialogue!==undefined) {validateDualDialogue(block.dualDialogue,block.content); return block.dualDialogue;}
    const raw:unknown=JSON.parse(block.content);
    const record=(v:unknown):Record<string,unknown> => {
        if (!v || typeof v!=='object' || Array.isArray(v)) throw new Error('Invalid dual dialogue.');
        return v as Record<string,unknown>;
    };
    const root=record(raw);
    if (Object.keys(root).some(k => !['left','right','LEFT','RIGHT'].includes(k)) ||
        ('left' in root && 'LEFT' in root) || ('right' in root && 'RIGHT' in root)) throw new Error('Invalid dual dialogue.');
    const side=(name:DualSide):DualParagraph[] => {
        const data=record(root[name] ?? root[name.toUpperCase()]);
        if (Object.keys(data).some(k => !['character','dialogue','CHARACTER','DIALOGUE'].includes(k)) ||
            ('character' in data && 'CHARACTER' in data) || ('dialogue' in data && 'DIALOGUE' in data)) throw new Error('Invalid dual dialogue.');
        return (['character','dialogue'] as const).map(type => {
            const text=data[type] ?? data[type.toUpperCase()];
            if (typeof text!=='string') throw new Error('Invalid dual dialogue.');
            return {id:`${block.id}:${name}:${type}`,type,content:text};
        });
    };
    return {schemaVersion:1,paragraphAttributes:{Type:'General'},left:side('left'),right:side('right')};
}
export function withDual(block:ScriptBlockData, data:ScriptDualDialogue):ScriptBlockData {
    validateDualDialogue(data);
    return {...block,type:'dual-dialogue',content:dualText(data),richText:undefined,dualDialogue:data};
}
/** Native007730a0 keeps separate child streams at+20/+28. This is Story's
 * boundary-join policy within one stream, not a native coordinate conversion. */
export function joinDualParagraphs(data:ScriptDualDialogue,side:DualSide,id:string,direction:'backward'|'forward'):
    {data:ScriptDualDialogue;paragraphId:string;offset:number}|undefined {
    validateDualDialogue(data);
    const index=data[side].findIndex(p=>p.id===id);
    if(index<0)return;
    const first=direction==='backward'?index-1:index;
    const before=data[side][first],after=data[side][first+1];
    // Cue/parenthetical boundaries keep their roles. Alternatives belong to
    // their original paragraph; joining must never discard inactive choices.
    if(!before||!after||before.type!=='dialogue'||after.type!=='dialogue'||before.alternates||after.alternates)return;
    const merged=mergeRichParagraphs(before,after) as DualParagraph;
    const parts=[...data[side]];parts.splice(first,2,merged);
    return {data:{...data,[side]:parts},paragraphId:before.id,offset:before.content.length};
}
export function mapDual(block:ScriptBlockData, update:(paragraph:DualParagraph,side:DualSide)=>DualParagraph):ScriptBlockData {
    const data=dualFor(block);
    return withDual(block,{...data,left:data.left.map(p => update(p,'left')),right:data.right.map(p => update(p,'right'))});
}
export function replaceDualText(block:ScriptBlockData, oldText:string, newText:string, all=false, paragraphId?:string,revisionId?:string,trackDeletions=false):ScriptBlockData {
    if (!oldText) return block;
    let found=false;
    return mapDual(block,p => {
        if (paragraphId && p.id!==paragraphId || !all && found) return p;
        let result=p, offset=0, index:number;
        while ((index=result.content.indexOf(oldText,offset))>=0) {
            found=true;
            const content=result.content.slice(0,index)+newText+result.content.slice(index+oldText.length);
            result=replaceBlockText(result,content,revisionId,trackDeletions,observedTextEdit(result.content,content,index,index+oldText.length)) as DualParagraph;
            offset=index+newText.length; if (!all) break;
        }
        return result;
    });
}
export function scriptTextFields(block:ScriptBlockData):{text:string;paragraphId?:string}[] {
    if (block.type!=='dual-dialogue') return [{text:block.content}];
    try {const data=dualFor(block); return [...data.left,...data.right].map(p => ({text:p.content,paragraphId:p.id}));}
    catch {return [];}
}
